import React from "react";

import EtEtTrinityIcon6SVGComp from "@/assets/img/icons/svgComp/EtEtTrinityIcon6SVGComp";
import EtWabReflectionIcon1SVGComp from "@/assets/img/icons/svgComp/EtWabReflectionIcon1SVGComp";
import EtWabConversationIcon1SVGComp from "@/assets/img/icons/svgComp/EtWabConversationIcon1SVGComp";
import EtWabCreationIcon1SVGComp from "@/assets/img/icons/svgComp/EtWabCreationIcon1SVGComp";

const EtTrinityOnboardingWelcomeContent = () => {
  // Author - Atul Pandey
  // First Created - 11 Mar 2026
  // Purpose - reusable welcome content block for Purpose Native onboarding
  // Updated - 12 Mar 2026 - Refined Trinity Approach section content

  return (
    <div className="space-y-4">

      {/* 1️⃣ Opening narrative */}
      <div className="bg-orange-50 border border-orange-100 rounded p-4">
        <div className="flex items-center">
          <EtEtTrinityIcon6SVGComp className="h-9 w-9 text-orange-600" />
          <p className="ml-3 text-lg font-bold text-gray-800">The Trinity Approach</p>
        </div>

        <div className="mt-3 space-y-3">
          <p className="text-sm text-gray-700 leading-6">
            Trinity is designed to serve tech led leaders in systematically and continuously unlocking higher growth by stepping up their own professional performance levels as well as lifting the performance of their teams.
          </p>

          <p className="text-sm text-gray-700 leading-6">
            Trinity is the natural companion of every growth trail or intervention you undertake with Eternity, where growth performance is triggered through human to human engagement. We believe that a human touch is essential to unlocking untapped potential, while AI amplifies the orchestration and growth scale across an organization.
          </p>
        </div>
      </div>

      {/* 2️⃣ Human performance context */}
      <div className="bg-gray-50 border border-gray-200 rounded p-4">
        <p className="text-lg font-bold text-gray-800">Rethinking Human Performance</p>

        <div className="mt-3 space-y-3">
          <p className="text-sm text-gray-700 leading-6">
            Human performance gaps are typically viewed as either a skill gap or a behavior gap problem. The usual response is training to address skill gaps and coaching or mentoring to address behavior gaps.
          </p>

          <p className="text-sm text-gray-700 leading-6">
            We believe this approach is flawed. In tech led organizations, contexts are not fixed. Growth depends not only on consistency of execution, but also on constant innovation, thought leadership, stronger ownership, faster execution, deeper client trust, and the ability to take on more with less.
          </p>
        </div>
      </div>

      {/* 3️⃣ Mindset reset */}
      <div className="bg-white border border-gray-200 rounded p-4">
        <p className="text-lg font-bold text-gray-800">The Mindset Reset</p>

        <div className="mt-3 space-y-3">
          <p className="text-sm text-gray-700 leading-6">
            Purpose Native growth is not about more training or more easy pills. It begins with a deeper induction that activates three interconnected mindset muscles: thinking, habits, and purpose orientation.
          </p>

          <p className="text-sm text-gray-700 leading-6">
            The primary vehicle of change is habits. When you begin a Trinity journey, you begin activating the thinking habits that help separate you from the crowd and strengthen how you create growth.
          </p>
        </div>
      </div>

      {/* 4️⃣ Three foundational thinking habits */}
      <div>
        <p className="text-lg font-bold text-gray-800">
          Three Foundational Thinking Habits
        </p>

        <p className="text-sm text-gray-600 mt-1">
          This is where the Trinity journey begins for you, your teams, and your organization.
        </p>

        <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">

          {/* Reflections */}
          <div className="bg-gray-50 border border-gray-200 rounded p-3 hover:bg-orange-50 transition">
            <div className="flex items-center">
              <EtWabReflectionIcon1SVGComp className="h-8 w-8 text-orange-600" />
              <p className="ml-2 text-sm font-bold text-gray-800">Reflections</p>
            </div>

            <p className="text-sm text-gray-600 mt-2 leading-5">
              Build the habit of thinking more clearly and seeing the work and growth ahead more intentionally.
            </p>
          </div>

          {/* Conversations */}
          <div className="bg-gray-50 border border-gray-200 rounded p-3 hover:bg-orange-50 transition">
            <div className="flex items-center">
              <EtWabConversationIcon1SVGComp className="h-8 w-8 text-orange-600" />
              <p className="ml-2 text-sm font-bold text-gray-800">Conversations</p>
            </div>

            <p className="text-sm text-gray-600 mt-2 leading-5">
              Build the habit of engaging with greater presence, trust, and leadership across clients, teams, and partners.
            </p>
          </div>

          {/* Creations */}
          <div className="bg-gray-50 border border-gray-200 rounded p-3 hover:bg-orange-50 transition">
            <div className="flex items-center">
              <EtWabCreationIcon1SVGComp className="h-8 w-8 text-orange-600" />
              <p className="ml-2 text-sm font-bold text-gray-800">Creations</p>
            </div>

            <p className="text-sm text-gray-600 mt-2 leading-5">
              Build the habit of shaping original thinking into work products, ideas, and outputs that move growth forward.
            </p>
          </div>
        </div>
      </div>

    </div>
  );
};

export default EtTrinityOnboardingWelcomeContent;